import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  NavigateNext,   
  NavigateBefore  
} from '@mui/icons-material';
import { fetchDocumentInstances, deleteDocumentInstance, createDocumentInstance } from '@/store/slices/documentInstancesSlice';
import { DocumentUploadModal, DocumentMetadataViewModal } from '@/components/documents';
import type { RootState, AppDispatch } from '@/store';
import type { DocumentInstance, DocumentInstanceCreate } from '@/types';

const DocumentListView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { items: documents, loading, error, total } = useSelector((state: RootState) => state.documentInstances);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [selectedDocumentMetadata, setSelectedDocumentMetadata] = useState<Record<string, unknown> | null>(null);
  const [selectedDocumentFilename, setSelectedDocumentFilename] = useState<string>('');
  const [searchTerm] = useState('');
  
  const [page, setPage] = useState(1);
  const pageSize = 10;

  
  useEffect(() => {
    dispatch(fetchDocumentInstances({ page: page, size: pageSize, search: searchTerm }));
  }, [dispatch, searchTerm, page]);

  const handleCreateDocument = () => {
    setUploadModalOpen(true);
  };

  const handleUploadDocument = async (documentData: DocumentInstanceCreate) => {
    try {
      await dispatch(createDocumentInstance(documentData)).unwrap();
      setUploadModalOpen(false);
      dispatch(fetchDocumentInstances({ page: page, size: pageSize, search: searchTerm }));
    } catch (error) {
      console.error('Failed to upload document:', error);
    }
  };

  const handleEditDocument = (id: number) => {
    console.log('Edit document:', id);
  };

  const handleDeleteDocument = async (id: number, filename: string) => {
    if (window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      try {
        await dispatch(deleteDocumentInstance(id)).unwrap();
        // Refresh list
        dispatch(fetchDocumentInstances({ page: page, size: pageSize, search: searchTerm }));
      } catch (error) {
        console.error('Failed to delete document:', error);
      }
    }
  };

  const handleViewMetadata = (document: DocumentInstance) => {
    setSelectedDocumentMetadata(document.metadata_json);
    setSelectedDocumentFilename(document.filename);
    setMetadataModalOpen(true);
  };

  const handleDocumentClick = (id: number) => {
    navigate(`/documents/${id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getQCStatus = (document: DocumentInstance) => {
    if (!document.workflow_runs || document.workflow_runs.length === 0) {
      return null;
    }

    const latestRun = document.workflow_runs[0];
    const qcSteps = latestRun.step_objects?.filter(step => step.node_type === 'QC') || [];
    
    if (qcSteps.length === 0) {
      return null; 
    }

    const allPassed = qcSteps.filter(i => i.pass_fail_output).every(step => step.pass_fail_output === 'PASS');
    const anyFailed = qcSteps.filter(i => i.pass_fail_output).some(step => step.pass_fail_output === 'FAIL');

    if (anyFailed) {
      return { status: 'failed', label: 'Failed' };
    } else if (allPassed) {
      return { status: 'passed', label: 'Passed' };
    } else {
      return { status: 'running', label: 'Running' };
    }
  };


  const handleNextPage = () => {
    if (page * pageSize < total) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  if (loading && documents.length === 0 && page === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Documents
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDocument}
          sx={{ minWidth: 150 }}
        >
          Upload Document
        </Button>
      </Box>


      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document Title</TableCell>
                <TableCell>Workflow</TableCell>
                <TableCell>QC Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Metadata</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {documents.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                      <Typography variant="h6" color="text.secondary">
                        No documents found
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleCreateDocument}
                      >
                        Upload Document
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((document: DocumentInstance) => {
                  const qcStatus = getQCStatus(document);
                  
                  return (
                    <TableRow key={document.id} hover>
                      <TableCell>
                        <Typography 
                          variant="subtitle2" 
                          fontWeight="medium"
                          sx={{ 
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => handleDocumentClick(document.id)}
                        >
                          {document.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {document.workflow_title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {qcStatus ? (
                          <Chip
                            icon={qcStatus.status === 'passed' ? <PassIcon /> : <FailIcon />}
                            label={qcStatus.label}
                            color={qcStatus.status === 'passed' ? 'success' : 'error'}
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(document.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          onClick={() => handleViewMetadata(document)}
                          sx={{ textTransform: 'none' }}
                        >
                          View metadata
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditDocument(document.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDocument(document.id, document.filename)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {loading && (
             <Box sx={{ width: '100%', p: 2, display: 'flex', justifyContent: 'center' }}>
                 <CircularProgress size={24} />
             </Box>
        )}
        
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {documents.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, total)} of {total} documents
          </Typography>
          
          <Stack direction="row" spacing={2}>
            <Button
                variant="outlined"
                size="small"
                onClick={handlePrevPage}
                disabled={page === 1 || loading}
                startIcon={<NavigateBefore />}
            >
                Previous
            </Button>
            <Button
                variant="contained" 
                size="small"
                onClick={handleNextPage}
                disabled={page * pageSize >= total || loading}
                endIcon={<NavigateNext />}
            >
                Next
            </Button>
          </Stack>
        </Box>
      </Paper>

      <DocumentUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadDocument}
      />

      <DocumentMetadataViewModal
        open={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        metadata={selectedDocumentMetadata}
        filename={selectedDocumentFilename}
      />
    </Box>
  );
};

export default DocumentListView;